import { useMemo } from "react";

import type { Invoice } from "~/shared/schemas/invoice-schema";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import { applyGridPrefs } from "~/shared/utils/apply-grid-prefs.ts";
import { getDefaultPaymentGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { getPaymentColumns } from "../utils/get-payment-columns.tsx";

type UsePaymentColumnsArgs = {
	handleDeselect: (id: number | string) => void;
	handleSelect: (id: number | string, selectedByAction?: boolean) => void;
	selectRecord: Record<string | number, boolean>;
	isAllSelected: boolean;
	handleSelectAll: (selected: boolean) => void;
	handleDocumentClick: (invoice: Invoice) => void;
	isInternal: boolean;
	columnPrefs?: GridPrefColumn[];
};

/**
 * Builds the Payment (Invoices) board columns and resolves their
 * visibility, order, and width against the user's saved server preferences.
 *
 * Mirrors `useLoadColumns`: the canonical default prefs are merged with the
 * user's saved prefs (saved value wins for `isVisible` / `widthSize` /
 * `ordinalPosition`; the default `visibility` always wins so deployment-scope
 * changes take effect on stale prefs; saved-only columns absent from defaults
 * are preserved for forward compatibility) and then run through
 * `applyGridPrefs`, which pins the select (`""`) and `docs` columns, enforces
 * the minimum width, and orders the data columns.
 */
export const usePaymentColumns = ({
	handleDeselect,
	handleSelect,
	selectRecord,
	isAllSelected,
	handleSelectAll,
	handleDocumentClick,
	isInternal,
	columnPrefs,
}: UsePaymentColumnsArgs): {
	allColumns: BoardColumn[];
	visibleColumns: BoardColumn[];
} => {
	const allColumns = useMemo<BoardColumn[]>(() => {
		const paymentColumns = getPaymentColumns({
			handleDeselect,
			handleSelect,
			selectRecord,
			isAllSelected,
			handleSelectAll,
			handleDocumentClick,
		}) as BoardColumn[];

		const defaults =
			getDefaultPaymentGridPrefs(isInternal)[`${GridIdentifiers.Payment}`]
				?.columns ?? [];
		const defaultsByField = new Map(defaults.map((p) => [p.columnId, p]));
		const savedByField = new Map(
			(columnPrefs ?? []).map((p) => [p.columnId, p]),
		);

		// Merge defaults with the user's saved prefs. `isVisible`, `widthSize`,
		// and `ordinalPosition` are user preferences and the saved value wins.
		// `visibility` is column metadata — defaults always win so that updates
		// to a column's deployment visibility take effect for users with stale
		// persisted prefs from older defaults.
		const mergedById = new Map<string, GridPrefColumn>();
		for (const def of defaults) {
			const saved = savedByField.get(def.columnId);
			mergedById.set(
				def.columnId,
				saved ? { ...saved, visibility: def.visibility } : def,
			);
		}
		// Preserve any saved entries that aren't present in defaults (forward
		// compat — a saved column id we no longer ship). Keep saved visibility
		// for these since there's no default to defer to.
		for (const saved of savedByField.values()) {
			if (!defaultsByField.has(saved.columnId)) {
				mergedById.set(saved.columnId, saved);
			}
		}
		const paymentPrefs = [...mergedById.values()];

		return applyGridPrefs(paymentColumns, paymentPrefs, isInternal);
	}, [
		handleDeselect,
		handleSelect,
		selectRecord,
		isAllSelected,
		handleSelectAll,
		handleDocumentClick,
		isInternal,
		columnPrefs,
	]);

	const visibleColumns = useMemo(
		() => allColumns.filter((col) => col.show),
		[allColumns],
	);

	return { allColumns, visibleColumns };
};
